import type { FC } from "react";
import type React from "react";
import { useCallback, useMemo } from "react";

import styled from "styled-components";

import { VerticalLegendsWithSearch } from "app/components/React/legend/vertical-legends-with-search/VerticalLegendsWithSearch";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { DEFAULT_UN_CHECK_COLOR, MAX_SELECTION } from "../consts";
import type { LegendItem } from "../widgets/VisitsOvertime/types";

const placeholderText = {
    searchPlaceHolder: translate("analysis.content.lob.legend.search.placeholder"),
    searchLabel: translate("analysis.content.lob.legend.search.label"),
};

const Container = styled.div`
    width: 260px;
    min-width: 260px;
    height: 100%;
`;

interface ILOBLegendsProps {
    legendItems: any[];
    setLegendItems: React.Dispatch<React.SetStateAction<any[]>>;
    columnTitles: [string, string];
    eventName: string;
    height?: string;
    hasSearch?: boolean;
}

const LOBLegends: FC<ILOBLegendsProps> = ({
    legendItems,
    setLegendItems,
    columnTitles,
    eventName,
    height = "135px",
    hasSearch,
}) => {
    const toggleSeries = useCallback((clicked) => {
        allTrackers.dispatchTrackEvent(
            "Click Filter",
            clicked.visible ? "uncheck" : "check",
            `${eventName}/${clicked.name}`,
        );
        setLegendItems((prev) =>
            prev.map((li) => {
                if (li.name !== clicked.name) {
                    return li;
                }
                const isTotal = li.isTotal;
                const activeCount = prev.filter((i) => i.visible && !i.isTotal).length;
                const newVisible = isTotal ? !li.visible : !li.visible && activeCount < MAX_SELECTION;
                return {
                    ...li,
                    visible: newVisible,
                    color: newVisible ? li.assignedColor : DEFAULT_UN_CHECK_COLOR,
                };
            }),
        );
    }, []);

    const { mainLegendItems, totalLegendItem } = useMemo(() => {
        const mainLegendItems: LegendItem[] = [];
        let totalLegendItem: LegendItem | null = null;

        for (const item of legendItems) {
            if (item.isTotal) {
                totalLegendItem = item;
            } else {
                mainLegendItems.push(item);
            }
        }

        return { mainLegendItems, totalLegendItem };
    }, [legendItems]);
    return (
        <Container>
            <VerticalLegendsWithSearch
                legendItems={mainLegendItems}
                toggleSeries={toggleSeries}
                maxSelection={MAX_SELECTION}
                legendItemsHeight={height}
                text={placeholderText}
                titles={columnTitles}
                otherLegend={totalLegendItem}
                textMaxWidth="120px"
                subtitleMaxWidth="80px"
                legendsFadeHeight="25px"
                limitationReachedTooltip="analysis.content.lob.legend.tooltip"
                hasSearch={hasSearch}
            />
        </Container>
    );
};

export default LOBLegends;
